import React, {useState } from 'react';
import { useDispatch } from 'react-redux';
import AppTextInput, {
    AppTextInputCallables,
} from '../components/common/input/AppTextInput';
import AppButton from '../components/common/input/AppButton';
import AppCheckbox from '../components/common/input/AppCheckbox';
import { useLazyGetCheckoutIdQuery } from '../app/services/api';
import { useAppSelector } from '../app/hooks';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/common/numberUtils';
import { CheckoutDetails } from '../models/checkoutDetails';
import { setCheckoutDetails } from '../store/checkoutSlice';
import MainContainer from '../components/common/layout/MainContainer';
import { BrandStyling } from '../theme/brandStyling';

const CustomerDetails: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [textInputCallables, setTextInputCallables] =
        useState<AppTextInputCallables>();

    const { publicProfile, publicTeam } = useAppSelector(
        (state) => state.profile,
    );
    const { checkoutDetails } = useAppSelector((state) => state.checkout);

    const [getCheckoutIdTrigger] = useLazyGetCheckoutIdQuery();
    const [emailAddress, setEmailAddress] = useState<string>('');
    const [tipperName, setTipperName] = useState<string>('');
    const [nameErrorText, setNameErrorText] = useState<string>('');
    const [emailErrorText, setEmailErrorText] = useState<string>('');
    const [termsIsChecked, setTermsIsChecked] = useState<boolean>(true);

    const uniqueId = publicProfile?.id ?? publicTeam?.id;
    const accountType = publicProfile === undefined ? 'Team' : 'Member';
    const brandId = publicProfile?.brandId ?? publicTeam?.brandId ?? 1;
    const tipValue = checkoutDetails?.tipValue;
    const serviceCharge = checkoutDetails?.serviceCharge;

    const countryCode = publicProfile
        ? publicProfile.countryCode
        : publicTeam?.countryCode;
    const currency = countryCode === 'GB' ? 'GBP' : 'EUR';

    const isValidEmail = (email: string) => {
        return (email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)?.length ?? 0) > 0;
    };

    const redirectToPayment = async () => {
        let checkoutDetails: CheckoutDetails = {
            tipValue: tipValue,
            serviceCharge: serviceCharge,
            accountType: accountType,
            uniqueId: uniqueId,
            emailAddress: emailAddress,
            fullName: tipperName,
            currency: currency,
        };
        dispatch(setCheckoutDetails(checkoutDetails));

        let checkoutId: string = '';
        await getCheckoutIdTrigger(checkoutDetails)
            .unwrap()
            .then((resp) => {
                dispatch(setCheckoutDetails(checkoutDetails));
                checkoutId = resp.content;
                navigate('/payment');
            });
        dispatch(
            setCheckoutDetails({ ...checkoutDetails, checkoutId: checkoutId }),
        );
    };

    const validateTipperName = (value: string) => {
        if (value.length === 0) {
            setNameErrorText('Please enter a name');
        } else {
            setNameErrorText('');
        }
    };

    const validateEmail = (value: string) => {
        if (!isValidEmail(value)) {
            setEmailErrorText('Please enter a valid email address');
        } else {
            setEmailErrorText('');
        }
    };

    const hasValues = tipperName.length > 0 && emailAddress.length > 0;
    const hasError = nameErrorText.length > 0 || emailErrorText.length > 0;

    return (
        <MainContainer className="bg-background max-md:h-[100dvh] max-md:min-h-0" isBackIconShown={true} isHeaderShown={true} brandId={brandId}>
            <div className="flex-1 flex flex-col items-center">
                <div className="h-full flex flex-col items-center w-full px-5 py-5 md:px-0 md:py-0">
                    {BrandStyling.byBrand(brandId).bankIcon}
                    <h5 className="mx-3 mt-6 text-center">Total amount</h5>
                    <div className="text-5xl font-semibold mt-3">{`${formatCurrency(
                        (tipValue ?? 0) + (serviceCharge ?? 0),
                        currency,
                    )}`}</div>
                    <p className="text-blue-grey mt-4">
                        *Includes{' '}
                        {`${formatCurrency(serviceCharge ?? 0, currency)}`}{' '}
                        transaction fee
                    </p>
                    <AppTextInput
                        label="Card holder name"
                        placeholder="e.g. John Doe"
                        mode="text"
                        setValue={(value: string) =>
                            setTipperName(value.trim())
                        }
                        className={`w-full mt-12 mb-2`}
                        setCallables={setTextInputCallables}
                        errorText={nameErrorText}
                        brand={brandId}
                        onBlur={(event) =>
                            validateTipperName(event.target.value.trim())
                        }
                    />
                    <AppTextInput
                        label="Email address for receipt"
                        placeholder="e.g. email@example.com"
                        mode="text"
                        setValue={(value: string) =>
                            setEmailAddress(value.trim())
                        }
                        className={`w-full mb-2 mt-4`}
                        brand={brandId}
                        setCallables={setTextInputCallables}
                        errorText={emailErrorText}
                        onBlur={(event) =>
                            validateEmail(event.target.value.trim())
                        }
                    />
                </div>
            </div>
            <div className="w-full pb-10 px-5 md:pb-0 md:px-0 py-5 pt-5 md:py-0 md:pt-0">
                <div className="flex justify-center gap-2 items-center mt-5 mb-2 md:mb-6">
                    <div className="flex justify-center gap-2 items-center">
                        <AppCheckbox
                            value={termsIsChecked}
                            setValue={setTermsIsChecked}
                            branding={brandId}
                        />
                        <p
                            className='text-2xs pt-1 mr-4 ml-2 flex-grow text-sm text-dark-blue'
                        >
                            I agree to the{' '}
                            <a
                                href="https://www.urocked.com/customers-terms-conditions/"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Terms & Conditions
                            </a>
                        </p>
                    </div>
                </div>
                <AppButton
                    className="mt-2"
                    sizeClassName="w-full h-12"
                    value='Continue to payment'
                    isEnabled={
                        tipValue !== undefined &&
                        hasValues &&
                        termsIsChecked &&
                        !hasError
                    }
                    onClick={redirectToPayment}
                    branding={brandId}
                />
            </div>
        </MainContainer>
    );
};

export default CustomerDetails;
